import { CSSObject } from "@emotion/react";
import {
  constants,
  cssVariableStyles,
  mediaQueryStyles,
} from "@newsela/angelou";

import { fullGradientColors } from "@/components/AIComponents/AiComponents.style";

const ui = constants.colors.ui;
const status = constants.colors.status;
const borderColor = ui.core[600];
const white = ui.white[25];
const red = status.danger[700];
const footerBorderColorRGB = ui.neutral[100];
const { mediaQuery: breakpoints } = mediaQueryStyles;
const redIcon = cssVariableStyles.generateIconColorCss({
  primaryColor: red,
});

export const baseWrapperStyles: CSSObject = {
  backgroundColor: ui.white[25],
  borderRadius: "6px",
};

export const baseStyles = (showLunaAiBranding: boolean): CSSObject => ({
  background: !showLunaAiBranding
    ? white
    : `linear-gradient(
    281.41deg,
    ${fullGradientColors.position100.color} 0%,
    ${fullGradientColors.position50.color} 48.95%,
    ${fullGradientColors.position25.color} 73.43%,
    ${fullGradientColors.position0.color} 97.9%
  )`,
  border: `1px solid ${borderColor}`,
  padding: "1px",
  borderRadius: "8px",
  display: "flex",
  flexFlow: "column",
  marginBottom: "30px",
  top: "-10px",
  left: "-6px",
  width: "100%",
  position: "relative",
  zIndex: "2",
  "::before": {
    content: "''",
    height: "calc(100% + 32px)",
    border: `2px solid ${white}`,
    position: "absolute",
    left: "-5px",
    top: "-20px",
  },
});

export const headerStyles = (
  hasBody: boolean,
  showLunaAiBranding: boolean,
): CSSObject => {
  return {
    padding: "16px",
    background: !showLunaAiBranding
      ? ui.core[25]
      : `linear-gradient(
      102deg,
      ${fullGradientColors.position0.color}1A 0%,
      ${fullGradientColors.position25.color}1A 25%,
      ${fullGradientColors.position50.color}1A 50%,
      ${fullGradientColors.position100.color}1A 100%
    )`,
    borderBottom: `1px solid ${ui.neutral[100]}`,
    borderTopLeftRadius: "inherit",
    borderTopRightRadius: "inherit",
    borderBottomLeftRadius: hasBody ? "0px" : "inherit",
    borderBottomRightRadius: hasBody ? "0px" : "inherit",
    color: ui.neutral[600],
    display: "grid",
    alignItems: "center",
    gap: "0 10px",
    gridTemplateColumns: "auto 1fr auto",
    gridTemplateAreas: `
    'title combobox closeButton'
    `,
    [breakpoints.upTo("sm")]: {
      gridTemplateColumns: "1fr auto",
      gridTemplateRows: "35px 28px",
      gridTemplateAreas: `
      'title closeButton'
      'combobox combobox'
      `,
      paddingBlock: "12px",
    },
  };
};

export const buttonContainerStyles: CSSObject = {
  display: "flex",
  justifyContent: "flex-end",
};

export const closeButtonStyles: CSSObject = {
  gridArea: "closeButton",
};

export const titleStyles: CSSObject = {
  gridArea: "title",
  fontWeight: 700,
  verticalAlign: "middle",
};

export const comboboxWrapperStyles: CSSObject = {
  gridArea: "combobox",
};

const comboboxHeight = "28px";
const comboboxAccentColor = ui.core[600];
export const comboboxStyles: CSSObject = {
  display: "inline-block",
  fontSize: "12px",
  height: comboboxHeight,
  width: "100%",
  maxWidth: "300px",
  "& > div": {
    maxWidth: "100%",
  },
  "& > div:first-child": { margin: "0" },
  // customizing input field
  "& input[role='combobox']": {
    borderRadius: "8px",
    color: comboboxAccentColor,
    height: comboboxHeight,
    border: `1px solid ${ui.neutral[100]}`,
    width: "100%",
    "& + button": {
      height: comboboxHeight,
      color: comboboxAccentColor,
    },
  },
  // Ensure selected item in dropdown has high contrast
  "& [aria-selected='true'], & .selected, & .Combobox__selected": {
    color: ui.core[700], // dark blue for high contrast
    fontWeight: 700,
    background: ui.core[25],
  },
  "& ul[role='listbox']": {
    zIndex: "4",
  },
};

export const iconStyles: CSSObject = {
  width: "23px",
  verticalAlign: "middle",
  margin: "-2px 5px 0 0",
  path: {
    stroke: "currentColor",
    strokeWidth: 0.5,
  },
};

export const bodyStyles: CSSObject = {
  padding: "14px 20px",
};

export const translatedTextStyles = (isError: boolean): CSSObject => {
  const errorStyles: CSSObject = isError
    ? {
        color: red,
      }
    : {};

  return {
    "&&&&": {
      margin: "0",
      lineHeight: "150%",
      fontSize: "16px",
      ...errorStyles,
    },
  };
};

export const loadingSpinnerStyles: CSSObject = {
  width: 64,
  height: 64,
  color: ui.core[600],
  display: "block",
  margin: "0 auto",
  padding: 8,
};

export const loadingLabelStyles: CSSObject = {
  color: ui.core[600],
  textAlign: "center",
  "&&&&": {
    fontSize: "14px",
    margin: "0",
    lineHeight: "114%",
    fontWeight: 700,
  },
};

export const errorAIIconStyles: CSSObject = {
  marginTop: "-6px",
  marginRight: "6px",
  verticalAlign: "middle",
  ...redIcon,
};

export const footerStyles: CSSObject = {
  borderTop: `1px solid ${footerBorderColorRGB}`,
  padding: "14px 20px",
  verticalAlign: "middle",
};

export const footerAIIconStyles: CSSObject = {
  marginTop: "-2px",
  verticalAlign: "middle",
};

export const footerTextStyles: CSSObject = {
  color: ui.neutral[500],
  "&&&&": {
    margin: "0",
    lineHeight: "150%",
  },
};

export const buttonContainerCss: CSSObject = {
  marginTop: "16px",
  display: "grid",
  gap: "20px",
  gridTemplateColumns: "1fr 1fr",
};

export const featureButtonCss: CSSObject = {
  padding: "8px",
  justifyContent: "center",
  width: "100%",
};

export const disableFeatureToastTextCss: CSSObject = {
  "& div": {
    display: "inline-block",
  },
};

export const disableFeatureToastButtonCss: CSSObject = {
  textDecoration: "underline",
};
